import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { FilmIcon } from '../icons/FilmIcon';
import { PlayIcon } from '../icons/PlayIcon';
import { MagicWandIcon } from '../icons/MagicWandIcon';
import { RenderModal } from './RenderModal';

const PauseIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
    </svg>
);

// Draggable overlay component for text, logo, image
const DraggableOverlay = ({ clip, isSelected, onSelect, onMove, containerRef, playheadPosition = 0 }) => {
    const [dragging, setDragging] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });

    const handleMouseDown = (e) => {
        e.stopPropagation();
        onSelect(clip);
        setDragging(true);
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const currentX = (clip.x / 100) * rect.width;
        const currentY = (clip.y / 100) * rect.height;
        dragOffset.current = { x: e.clientX - rect.left - currentX, y: e.clientY - rect.top - currentY };
    };

    useEffect(() => {
        if (!dragging) return;
        const handleMouseMove = (e) => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return;
            const x = Math.max(0, Math.min(100, ((e.clientX - rect.left - dragOffset.current.x) / rect.width) * 100));
            const y = Math.max(0, Math.min(100, ((e.clientY - rect.top - dragOffset.current.y) / rect.height) * 100));
            onMove(clip.id, { x, y });
        };
        const handleMouseUp = () => setDragging(false);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }, [dragging, clip.id, onMove, containerRef]);

    // Compute translate so overlay stays within frame:
    // At x=0 → translateX=0 (left-aligned), x=50 → -50% (centered), x=100 → -100% (right-aligned)
    const tx = -(clip.x ?? 50);
    const ty = -(clip.y ?? 50);

    // Time-based fade factor honours the clip's own transition setting (used
    // for auto fade-out on the outro card, etc.).
    let fadeFactor = 1;
    if (clip.transition && clip.transition.type && clip.transition.type !== 'none') {
        const dur = clip.transition.duration || 0.5;
        const into = playheadPosition - clip.start;
        const remaining = (clip.start + clip.duration) - playheadPosition;
        if (clip.transition.type === 'fade-in' || clip.transition.type === 'cross-dissolve') {
            if (into < dur) fadeFactor = Math.max(0, Math.min(1, into / dur));
        }
        if (clip.transition.type === 'fade-out' || clip.transition.type === 'cross-dissolve') {
            if (remaining < dur) fadeFactor = Math.min(fadeFactor, Math.max(0, remaining / dur));
        }
    }

    const style = {
        left: `${clip.x ?? 50}%`,
        top: `${clip.y ?? 50}%`,
        transform: `translate(${tx}%, ${ty}%) rotate(${clip.rotation ?? 0}deg)`,
        opacity: (clip.opacity ?? 1) * fadeFactor,
        cursor: dragging ? 'grabbing' : 'grab',
    };

    const handleClick = (e) => {
        e.stopPropagation();
        onSelect(clip);
    };

    if (clip.type === 'text') {
        const baseFontSize = clip.fontSize || 48;
        // Use the user's chosen font size as-is. Newlines (\n) in the
        // content are preserved via `whiteSpace: pre-wrap` so the text
        // wraps to the next line instead of shrinking. The Inspector
        // Panel warns the user if their content exceeds 3 visual lines.
        return (
            <div
                className={`absolute select-none ${isSelected ? 'ring-2 ring-violet-500 ring-offset-1 ring-offset-transparent' : ''}`}
                style={{ ...style, color: clip.color, fontSize: `${baseFontSize}px`, fontFamily: clip.fontFamily || 'sans-serif', textShadow: '2px 2px 4px rgba(0,0,0,0.7)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '92%', lineHeight: 1.2, textAlign: 'center' }}
                onMouseDown={handleMouseDown}
                onClick={handleClick}
                data-testid={`overlay-${clip.id}`}
            >
                {clip.content || 'Text'}
            </div>
        );
    }

    // Logo or Image
    const src = clip.src || '';
    const w = clip.width ?? (clip.type === 'logo' ? 15 : 40);
    const h = clip.height ?? (clip.type === 'logo' ? 15 : 40);

    return (
        <div
            className={`absolute select-none ${isSelected ? 'ring-2 ring-violet-500 ring-offset-1 ring-offset-transparent' : ''}`}
            style={{ ...style, width: `${Math.min(w, 90)}%`, maxHeight: '90%' }}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            data-testid={`overlay-${clip.id}`}
        >
            {src && <img src={src} alt={clip.name || clip.type} className="w-full h-full object-contain pointer-events-none" draggable={false} />}
        </div>
    );
};

export const PreviewMonitor = ({ project, playheadPosition, isPlaying, setIsPlaying, setPlayheadPosition, onProjectUpdate, selectedClipId, onClipSelected, onUpdateClip, onGoToDashboard, onAutoSave }) => {
    const videoRef = useRef(null);
    const audioRefs = useRef({});
    const requestRef = useRef(0);
    const previewContainerRef = useRef(null);
    const overlayAreaRef = useRef(null);
    const timeline = project.timeline;

    const [activeVideoClip, setActiveVideoClip] = useState(null);
    const [isRenderModalOpen, setIsRenderModalOpen] = useState(false);
    const [videoObjectUrl, setVideoObjectUrl] = useState(null);

    // Compute the aspect ratio from project data
    const aspectRatio = useMemo(() => {
        const ar = project.aspectRatio || '16:9';
        const parts = ar.split(':').map(Number);
        return (parts[0] && parts[1]) ? parts[0] / parts[1] : 16 / 9;
    }, [project.aspectRatio]);

    const audioAssetMap = useMemo(() => {
        const map = new Map();
        for (const asset of project.assets) {
            if (asset.type === 'audio' && asset.data) {
                map.set(asset.id, asset.data);
            }
        }
        return map;
    }, [project.assets]);

    // Get all active overlay clips (text, logo, image) at current playhead
    const activeOverlayClips = useMemo(() => {
        if (!timeline) return [];
        return timeline.clips.filter(c =>
            (c.type === 'text' || c.type === 'logo' || c.type === 'image') &&
            playheadPosition >= c.start &&
            playheadPosition < c.start + c.duration
        );
    }, [timeline, playheadPosition]);

    const activeAudioClips = useMemo(() => {
         if (!timeline) return [];
        return timeline.clips.filter(c => 
            c.type === 'audio' && 
            playheadPosition >= c.start && 
            playheadPosition < c.start + c.duration
        );
    }, [timeline, playheadPosition]);
    
    const currentVideoClip = useMemo(() => {
        if (!timeline) return null;
        return timeline.clips.find(c => 
            c.type === 'video' && 
            playheadPosition >= c.start && 
            playheadPosition < c.start + c.duration
        ) || null;
    }, [timeline, playheadPosition]);

    const currentShot = useMemo(() => {
        if (!currentVideoClip) return null;
        const shots = project.scenes.flatMap(s => s.shots);
        return shots.find(s => s.id === currentVideoClip.shotId || s.id === currentVideoClip.assetId) || null;
    }, [currentVideoClip, project.scenes]);

    useEffect(() => {
        const newKey = currentVideoClip?.shotId || currentVideoClip?.assetId;
        const oldKey = activeVideoClip?.shotId || activeVideoClip?.assetId;
        if (newKey !== oldKey) {
            setActiveVideoClip(currentVideoClip);
            if (videoObjectUrl) {
                URL.revokeObjectURL(videoObjectUrl);
                setVideoObjectUrl(null);
            }
        }
    }, [currentVideoClip?.shotId, currentVideoClip?.assetId]);

    useEffect(() => {
        if (!currentShot?.video) return;
        const videoUrl = currentShot.video;
        if (videoUrl.startsWith('data:')) {
            setVideoObjectUrl(videoUrl);
        } else {
            setVideoObjectUrl(videoUrl);
        }
    }, [currentShot?.video]);

    useEffect(() => {
        if (!isPlaying) {
            cancelAnimationFrame(requestRef.current);
            return;
        }
        let lastTimestamp = null;
        const animate = (timestamp) => {
            if (lastTimestamp !== null) {
                const deltaSec = (timestamp - lastTimestamp) / 1000;
                setPlayheadPosition(prev => {
                    const next = prev + deltaSec;
                    return next >= (timeline?.duration || 10) ? 0 : next;
                });
            }
            lastTimestamp = timestamp;
            requestRef.current = requestAnimationFrame(animate);
        };
        requestRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(requestRef.current);
    }, [isPlaying, timeline?.duration, setPlayheadPosition]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !currentVideoClip || !currentShot?.video) return;
        const videoTimeOffset = playheadPosition - currentVideoClip.start;
        const speed = currentVideoClip.speed || 1;
        const targetTime = videoTimeOffset * speed;
        if (isPlaying) {
            video.playbackRate = speed;
            if (Math.abs(video.currentTime - targetTime) > 0.3) video.currentTime = targetTime;
            video.play().catch(() => {});
        } else {
            video.pause();
            video.currentTime = targetTime;
        }
    }, [isPlaying, playheadPosition, currentVideoClip, currentShot?.video]);

    useEffect(() => {
        for (const clip of activeAudioClips) {
            const audioEl = audioRefs.current[clip.id];
            if (!audioEl) continue;
            const audioTimeOffset = playheadPosition - clip.start;
            audioEl.volume = clip.volume ?? 1;
            if (isPlaying) {
                if (Math.abs(audioEl.currentTime - audioTimeOffset) > 0.3) audioEl.currentTime = audioTimeOffset;
                audioEl.play().catch(() => {});
            } else {
                audioEl.pause();
                audioEl.currentTime = audioTimeOffset;
            }
        }
        Object.entries(audioRefs.current).forEach(([id, el]) => {
            if (el && !activeAudioClips.find(c => c.id === id)) {
                el.pause();
            }
        });
    }, [isPlaying, playheadPosition, activeAudioClips]);

    const handleSeek = (e) => {
        const newTime = parseFloat(e.target.value);
        setPlayheadPosition(newTime);
    };

    const getTransitionStyle = () => {
        if (!currentVideoClip?.transition || currentVideoClip.transition.type === 'none') return { opacity: 1 };
        const { type, duration = 0.5 } = currentVideoClip.transition;
        const timeIntoClip = playheadPosition - currentVideoClip.start;
        const timeUntilEnd = (currentVideoClip.start + currentVideoClip.duration) - playheadPosition;
        if (type === 'fade-in' || type === 'cross-dissolve') {
            if (timeIntoClip < duration) return { opacity: timeIntoClip / duration };
        }
        if (type === 'fade-out' || type === 'cross-dissolve') {
            if (timeUntilEnd < duration) return { opacity: timeUntilEnd / duration };
        }
        return { opacity: 1 };
    };

    const handleOverlayMove = useCallback((clipId, pos) => {
        if (!onUpdateClip) return;
        const clip = timeline?.clips.find(c => c.id === clipId);
        if (clip) onUpdateClip({ ...clip, x: pos.x, y: pos.y });
    }, [onUpdateClip, timeline]);

    const handleOverlaySelect = useCallback((clip) => {
        if (onClipSelected) onClipSelected(clip);
    }, [onClipSelected]);

    return (
        <div className="flex-grow bg-black flex flex-col items-center justify-center p-4 relative min-h-0">
             {timeline?.clips.filter(c => c.type === 'audio').map((clip) => {
                const audioSrc = audioAssetMap.get(clip.assetId);
                return audioSrc ? <audio key={clip.id} ref={el => { if (el) audioRefs.current[clip.id] = el }} src={audioSrc} crossOrigin="anonymous"/> : null;
            })}

            <div ref={previewContainerRef} className="w-full h-full bg-black relative flex items-center justify-center" onClick={() => onClipSelected && onClipSelected(null)}>
                {/* Video content area — sized to match actual aspect ratio within container */}
                <div
                    ref={overlayAreaRef}
                    className="relative"
                    // Box always matches the visible video frame: size is
                    // height-driven (auto width from aspect-ratio), and
                    // maxWidth: 100% caps it when the container is narrower
                    // than the project aspect ratio. Earlier landscape code
                    // path used width:100% which over-sized the overlay area
                    // on wide panels, pushing logo overlays outside the
                    // visible video frame.
                    style={{ aspectRatio: aspectRatio, height: '100%', width: 'auto', maxWidth: '100%', maxHeight: '100%' }}
                >
                    {currentShot?.video && currentVideoClip?.subtype === 'video' ? (
                        <video 
                            ref={videoRef} 
                            src={videoObjectUrl || ''} 
                            className="w-full h-full object-contain transition-opacity duration-75" 
                            style={getTransitionStyle()}
                            crossOrigin="anonymous" 
                            playsInline 
                            muted={false} 
                        />
                    ) : currentShot && (currentVideoClip?.subtype === 'end' ? currentShot.endFrameImage : currentShot.startFrameImage) ? (
                        <img 
                            src={currentVideoClip?.subtype === 'end' ? currentShot.endFrameImage : currentShot.startFrameImage} 
                            className="w-full h-full object-contain transition-opacity duration-75" 
                            style={getTransitionStyle()}
                            alt="Shot Preview" 
                        />
                    ) : (
                        <div className="absolute inset-0" />
                    )}

                    {/* Draggable overlays — positioned relative to video content area */}
                    {activeOverlayClips.map(clip => (
                        <DraggableOverlay
                            key={clip.id}
                            clip={clip}
                            isSelected={selectedClipId === clip.id}
                            onSelect={handleOverlaySelect}
                            onMove={handleOverlayMove}
                            containerRef={overlayAreaRef}
                            playheadPosition={playheadPosition}
                        />
                    ))}
                </div>
            </div>

            <div className="w-full max-w-4xl absolute bottom-4 left-1/2 -translate-x-1/2 p-2 bg-black/50 rounded-lg flex items-center space-x-4">
                <button onClick={() => setIsPlaying(!isPlaying)} className="text-white p-2">
                    {isPlaying ? <PauseIcon className="w-6 h-6" /> : <PlayIcon className="w-6 h-6" />}
                </button>
                <span className="text-xs w-16 text-center">{playheadPosition.toFixed(2)}s</span>
                <input
                    type="range"
                    min="0"
                    max={timeline?.duration || 100}
                    step="0.01"
                    value={playheadPosition}
                    onChange={handleSeek}
                    className="w-full"
                />
                 <span className="text-xs w-16 text-center">{timeline?.duration.toFixed(2)}s</span>
                 <button 
                    onClick={() => {
                        // Stage 7: auto-save before opening the Render modal so the
                        // exact state being rendered is persisted.
                        if (onAutoSave) { try { onAutoSave({ reason: 'pre-render' }); } catch { /* noop */ } }
                        setIsRenderModalOpen(true);
                    }} 
                    className="text-white p-2 rounded-full hover:bg-violet-600/50 transition-colors" 
                    title="Render video"
                >
                    <MagicWandIcon className="w-6 h-6" />
                </button>
            </div>
            
            {isRenderModalOpen && (
                <RenderModal
                    project={project}
                    onClose={() => setIsRenderModalOpen(false)}
                    onProjectUpdate={onProjectUpdate}
                    onGoToDashboard={onGoToDashboard}
                />
            )}
        </div>
    );
};
